import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { i18n } from 'utils/i18n';
import { sanitize } from 'utils/validation';
import cn from 'classnames';
import MODAL from 'constants/modals';
import Button from 'components/Button';
import RentalTermsAndConditionsModal from 'components/modals/RentalTermsAndConditionsModal';
import PolicyTab from './PolicyTab';
import HelpSuggestion from './HelpSuggestion';

const PoliciesComponent = ({
  policies,
  backgroundArtURL,
  title,
  hideTermsConditionsCta,
  isRental,
  openModal,
  selectedPolicyIndex,
  setIndexOfPolicyComponentViaIndex,
  _testExpandedTabHTML, // internal test prop
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedTabHTML, setExpandedTabHTML] = useState(_testExpandedTabHTML || '');

  // Store previous selectedPolicyIndex for comparison (like prevProps)
  const prevSelectedPolicyIndexRef = useRef();

  const handleExpand = useCallback(
    ({ index }) => {
      const isCurrent = expandedIndex === index;
      const newIndex = !isCurrent ? index : null;
      setExpandedIndex(newIndex);

      const newContent = !isCurrent ? policies[index]?.content : '';
      setExpandedTabHTML(newContent);

      if (setIndexOfPolicyComponentViaIndex && selectedPolicyIndex !== index) {
        setIndexOfPolicyComponentViaIndex(index);
      }
    },
    [expandedIndex]
  );

  useEffect(() => {
    const prevSelectedPolicyIndex = prevSelectedPolicyIndexRef.current;
    //  If we have a policy index set it
    if (selectedPolicyIndex !== prevSelectedPolicyIndex && selectedPolicyIndex !== expandedIndex) {
      handleExpand({ index: selectedPolicyIndex });
    }

    prevSelectedPolicyIndexRef.current = selectedPolicyIndex;
  }, [selectedPolicyIndex, expandedIndex]);

  const openTermsAndConditionsModal = () => {
    openModal(MODAL.KEY_RENTAL_FACTS_TERMS_AND_CONDITIONS);
  };

  const sectionTitle = title || i18n('renting_at_this_location');

  return (
    // @dev fieldset and 0 opacity legend element is for accessibility compliance
    <section id='policies-component' className='policies-component component-theme--light-blue'>
      <h2
        className={cn('policies-component__title', {
          'rental-title': isRental,
        })}
      >
        {sectionTitle}
      </h2>
      <div className='policies-component__group'>
        <div className='policies-component__list'>
          {!hideTermsConditionsCta && (
            <Button link onClick={openTermsAndConditionsModal} className='policies-component__link'>
              {i18n('review_page_rental_policies_terms_and_conditions')}
            </Button>
          )}
          <ul>
            {policies?.map((policy, index) => (
              <li key={index}>
                <PolicyTab {...policy} index={index} expanded={expandedIndex === index} onToggle={handleExpand} />
              </li>
            ))}
          </ul>
          <HelpSuggestion />
        </div>
        <div className='policies-component__side-content'>
          {expandedTabHTML && (
            <article className='policies-component__article' dangerouslySetInnerHTML={sanitize(expandedTabHTML)} />
          )}
          {backgroundArtURL && (
            <div className='policies-component__background-art'>
              <img src={backgroundArtURL} alt='' />
            </div>
          )}
        </div>
      </div>
      {!hideTermsConditionsCta && <RentalTermsAndConditionsModal />}
    </section>
  );
};

PoliciesComponent.propTypes = {
  policies: PropTypes.array.isRequired,
  backgroundArtURL: PropTypes.string,
  backgroundArtAlt: PropTypes.string,
  setIndexOfPolicyComponentViaIndex: PropTypes.func,
  selectedPolicyIndex: PropTypes.number,
  title: PropTypes.string,
  openModal: PropTypes.func,
  termsConditions: PropTypes.string,
  hideTermsConditionsCta: PropTypes.bool,
};

export default PoliciesComponent;
